import { effect, Signal, useSignal } from '@preact/signals';
import type { ObjectValues, StorageDefinition } from 'utils/types.ts';
import { useEffect } from 'preact/hooks';

export const LocalStorageKeys = {
	LAST_SEARCH_LOCALITY: 'lastSearchLocality',
	LAST_LOCALITY: 'lastLocality', // Shared across locality inputs that allow latest locality suggestion
	LAST_SEARCH_QUERIES: 'lastSearchQueries',
	MY_LOCATION: 'myLocation',
	MY_PRECISE_LOCATION: 'myPreciseLocation',
	MESSENGER_REOPEN_FLAG: 'messengerReopenFlag',
	OFFER_CONTACT_BOX_REOPEN_FLAG: 'offerContactBoxReopenFlag',
	OFFER_SHOW_PHONE_REOPEN_FLAG: 'offerShowPhoneReopenFlag',
} as const;
export type LocalStorageKeys = ObjectValues<typeof LocalStorageKeys>;

/**
 * Kompletní seznam Local Storage klíčů používaných aplikací Sbazar
 */
export const SBAZAR_LOCAL_STORAGE: StorageDefinition[] = [
	{
		name: LocalStorageKeys.LAST_SEARCH_LOCALITY,
		description: 'Uložení poslední použité lokality při vyhledávání',
		type: 'Analytická',
		expiration: 'Neexpiruje',
	},
	{
		name: LocalStorageKeys.LAST_LOCALITY,
		description: 'Sdílená poslední lokalita napříč všemi vstupními poli pro lokality',
		type: 'Technická',
		expiration: 'Neexpiruje',
	},
	{
		name: LocalStorageKeys.LAST_SEARCH_QUERIES,
		description: 'Historie posledních vyhledávacích dotazů uživatele',
		type: 'Preferenční',
		expiration: 'Neexpiruje',
	},
	{
		name: LocalStorageKeys.MY_LOCATION,
		description: 'Uložená aktuální poloha uživatele',
		type: 'Analytická',
		expiration: 'Neexpiruje',
	},
	{
		name: LocalStorageKeys.MY_PRECISE_LOCATION,
		description: 'Přesná GPS poloha uživatele pro lokalizační služby',
		type: 'Analytická',
		expiration: 'Neexpiruje',
	},
];

export function getLocalStorageList(): StorageDefinition[] {
	return SBAZAR_LOCAL_STORAGE;
}
export class LocalStorage {
	static removeItem(key: LocalStorageKeys) {
		if (globalThis?.localStorage) {
			globalThis.localStorage.removeItem(key);
		}
	}

	static setItem(key: LocalStorageKeys, value: string) {
		if (globalThis?.localStorage) {
			globalThis.localStorage.setItem(key, value);
		}
	}

	static getItem(key: LocalStorageKeys) {
		if (globalThis?.localStorage) {
			return globalThis.localStorage.getItem(key) || undefined;
		}

		return undefined;
	}
}

type LocalStorageSignalOptions<T> = {
	defaultValue?: T | null;
	transformToData: (value: string | null | undefined) => T;
	transformToString: (value: NonNullable<T>) => string;
};

export class LocalStorageSignal<T> extends Signal<T> {
	key: LocalStorageKeys;

	constructor(
		key: LocalStorageKeys,
		options: LocalStorageSignalOptions<T> = {
			transformToData: (str) => JSON.parse(str ?? 'null'),
			transformToString: (value) => JSON.stringify(value),
		},
	) {
		super(options.transformToData(LocalStorage.getItem(key)) ?? options.defaultValue ?? undefined);
		this.key = key;

		effect(() =>
			this.value === null || this.value === undefined || Number.isNaN(this.value)
				? LocalStorage.removeItem(this.key)
				: LocalStorage.setItem(this.key, options.transformToString(this.value)),
		);
	}
}

export function useLocalStorageValue<T>(signal: LocalStorageSignal<T>) {
	const isMounted = useSignal(false);
	useEffect(() => {
		isMounted.value = true;
	}, []);

	return { isMounted, signal };
}
